/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import QueryString from 'qs';

import { getEntryBrandCookie } from '../attribution/attributionHelpers';
import { getFeatureFlag } from '../../../state/ducks/App/ducks/Config/Config-Selectors';
import useBrowserUUID from '../useBrowserUUID';
import validatePassport from '../validatePassport';
import { getPassportSubscriptionStatus, getPassportData } from '../../../state/ducks/Passport/Passport-Selectors';
import { getIsAuthenticatedStatus, getIsPassportUserLoggedIn } from '../../../state/ducks/Checkout/ducks/Global/Global-Selectors';

/**
 * @description - used to make a useQuery call and acts like a interceptor for experiment service and aggregator service.
 * @param { string } queryName - findCategoryById|findCategoryPageByUrl|findProductsBySearchTerm
 * @returns { object } - {  targeting, context, isGraphqlTargetingEnabled  }
 */

const useExperimentServiceAttributes = ({
    queryName,
}) => {
    const location = useLocation();
    const isExperimentsServiceEnabled = useSelector(getFeatureFlag('is-experiments-service-enabled-pwa'))?.[queryName];

    const isClient = typeof window !== 'undefined';
    const browserUUID = (isClient && window.localStorage) ? useBrowserUUID() : '';
    const pageType = (isClient && window.pageType) ? window.pageType : '';
    const pageURL = (isClient && window.location) ? window.location.href : '';
    const query = QueryString.parse(location.search?.replace('?', ''));
    const isPriceFromGql = useSelector(getFeatureFlag('is-price-from-gql'));
    const experimentService = Boolean((isExperimentsServiceEnabled || isPriceFromGql) && isClient);
    const isGraphqlTargetingEnabled = experimentService || useSelector(getFeatureFlag('is-gql-targeting-enabled-v2'))?.[queryName];

    const entryBrand = getEntryBrandCookie() || '';

    const isPassportAdded = useSelector(getPassportSubscriptionStatus);
    const passportData = useSelector(getPassportData);
    const isPassportMember = validatePassport('isActivePassportUser', passportData);
    const isPassportUser =  useSelector(getIsPassportUserLoggedIn);
    const isAuthenticated = useSelector(getIsAuthenticatedStatus);
    let customerTypeValue = [];
    if ((isPassportMember && isPassportUser) || isPassportAdded) {
        customerTypeValue = [{
            key: 'CUSTOMER_TYPE',
            value: 'Passport',
        }];
    } else if (isAuthenticated) {
        customerTypeValue = [{
            key: 'CUSTOMER_TYPE',
            value: 'Registered',
        }];
    } else {
        customerTypeValue = [{
            key: 'CUSTOMER_TYPE',
            value: 'Guest',
        }];
    }

    const targeting =  isGraphqlTargetingEnabled ? [
        { key: 'entryBrand', value: entryBrand },
        { key: 'url', value: location.pathname },
        { key: 'browserUUID', value: browserUUID },
        { key: 'anonymousId', value: browserUUID },
        { key: 'pageType', value: queryName === 'findProductsBySearchTerm' ? 'searchterm' : pageType },
        ...customerTypeValue,
    ] : [];

    // to handle re-render when URL change for product facets
    // pageURL with query params works only in POST call
    // pageURL is not required for findCategoryPageByUrl, Since its passed as separate variable (url)
    if (experimentService && queryName !== 'findCategoryPageByUrl') {
        targeting.push({ key: 'pageURL', value: pageURL });
    }

    if (isGraphqlTargetingEnabled && query?.isTester) {
        targeting.push({ key: 'isTester', value: String(query.isTester) });
    }

    const context = { api: experimentService ? 'experiments' : 'aggregator' };
    return {
        targeting, context, isGraphqlTargetingEnabled, experimentService, dynamicPriceInGql: isPriceFromGql,
    };
};

export default useExperimentServiceAttributes;
